import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

// L12.06 — Budget Overruns (read-only).
// As-built note: there is no dedicated `budget_overrun_approvals` table. A budget
// overrun is enforced as a business rule (M12 `budgetOverrunBlock`) that raises an
// exception of type BUDGET_EXCEEDED / COST_VARIANCE. This screen surfaces those
// exceptions as a focused, read-only overrun register sourced from `/v1/sh/exceptions`.

interface ExceptionRow {
  readonly exceptionId: string;
  readonly exceptionTypeCode: string;
  readonly severityCode: string;
  readonly status: string;
  readonly description: string;
  readonly reviewId: string | null;
  readonly jobNumber: string | null;
  readonly resolutionRemarks: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

const OVERRUN_TYPES = new Set(['BUDGET_EXCEEDED', 'COST_VARIANCE']);

export function ShBudgetOverrunsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ExceptionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await apiRequest<ExceptionRow[]>('/v1/sh/exceptions', { token });
      setRows((Array.isArray(all) ? all : []).filter((r) => OVERRUN_TYPES.has(r.exceptionTypeCode)));
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="sh-budget-overruns">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/budget" data-testid="back-budget">{t('sh.overruns.backBudget')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.overruns.title')}</h1>
      </div>

      <p style={{ color: 'var(--color-text-muted)', marginBottom: 12, fontSize: 13 }}>
        {t('sh.overruns.intro')} <Link to="/sh/exceptions">{t('sh.overruns.exceptionRegister')}</Link>{t('sh.overruns.introEnd')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      {rows.length === 0 ? (
        <p data-testid="overruns-empty" style={{ color: 'var(--color-text-muted)' }}>{t('sh.overruns.empty')}</p>
      ) : (
        <DataTable<ExceptionRow>
          ariaLabel="overruns-table"
          rows={rows}
          rowKey={(r) => r.exceptionId}
          rowTestId={(r) => `overrun-row-${r.exceptionId}`}
          columns={[
            { key: 'type', header: t('sh.common.type'), render: (r) => r.exceptionTypeCode },
            { key: 'severity', header: t('sh.common.severity'), render: (r) => (
              <span style={{ color: r.severityCode === 'CRITICAL' ? 'var(--color-danger)' : r.severityCode === 'HIGH' ? 'var(--color-warning)' : 'inherit' }}>
                {r.severityCode}
              </span>
            ) },
            { key: 'status', header: t('sh.common.status'), render: (r) => r.status },
            { key: 'description', header: t('sh.common.description'), render: (r) => (
              <span style={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.description}
              </span>
            ) },
            { key: 'review', header: t('sh.common.reviewCol'), render: (r) => (r.reviewId ? <Link to={`/sh/review/${r.reviewId}`}>{r.jobNumber ?? '—'}</Link> : '—') },
            { key: 'raised', header: t('sh.common.raised'), render: (r) => <span style={{ fontSize: 12 }}>{at(r.createdAt)}</span> },
            { key: 'resolved', header: t('sh.common.resolved'), render: (r) => <span style={{ fontSize: 12 }}>{r.resolvedAt ? at(r.resolvedAt) : '—'}</span> },
          ]}
        />
      )}
    </main>
  );
}
